import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { usePromotionBanners } from "../admin/contexts/PromotionBannerContext";
import { useSiteSearch } from "../context/SiteSearchContext";
import { useMessageModal } from "../admin/contexts/MessageModalContext";

import axios from "axios";
import "../assets/style.css";
import "../assets/Header.css";

const Header: React.FC = () => {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const IMAGE = import.meta.env.VITE_LYRAZONE_IMAGE_URL;
  const { banners, loading, getBannerImage } = usePromotionBanners();
  const validBanners = useMemo(() => banners || [], [banners]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [subscriber, setSubscriber] = useState<any>(null);
  const [userImage, setUserImage] = useState<string | null>(null);
  const [text, setText] = useState("");
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { performSearch } = useSiteSearch();
  const { showModal } = useMessageModal();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef<HTMLLIElement | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const storedUser = JSON.parse(
    localStorage.getItem("subscriber_user") || "null",
  );

  const removeToken = async () => {
    try {
      await axios.post(`${BASE}/logout`, {}, { withCredentials: true });

      setSubscriber(null);
      localStorage.removeItem("subscriber_user");
      sessionStorage.removeItem("userdata");

      navigate("/signin");
    } catch (error) {
      console.error("Logout failed");
    }
  };

  const handleProtectedNavigation = (path: string) => {
    if (!storedUser) {
      showModal({
        type: "warning",
        title: "Warning",
        message: "You are not logged in, please login first.",
      });
      return;
    }

    navigate(path);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (validBanners.length <= 1) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % validBanners.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [validBanners]);

  useEffect(() => {
    if (activeIndex >= validBanners.length) {
      setActiveIndex(0);
    }
  }, [validBanners, activeIndex]);

  useEffect(() => {
    if (validBanners.length <= 1 || isHovered) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % validBanners.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [validBanners, isHovered]);

  useEffect(() => {
    const fetchUserImage = async () => {
      try {
        const res = await axios.get(`${BASE}/userdata`, {
          withCredentials: true,
        });

        const user = res.data.userData[0];
        setSubscriber(user);
        setUserImage(res.data.userData[0]?.image || null);
      } catch (error) {
        console.error("Failed to fetch user image", error);
      }
    };

    fetchUserImage();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSearch = async () => {
    if (!text.trim()) return;

    await performSearch(text);
    navigate(`/search?q=${encodeURIComponent(text.trim())}`);
    setIsSearchOpen(false);
    closeMobileMenu();
  };

  return (
    <header>
      <div className="top-menubar">
        <div className="topmenu">
          <div className="container">
            <div className="row">
              <div className="col-md-7">
                <ul className="list-inline top-contacts">
                  <li>
                    <i className="fa fa-envelope"></i> Email:{" "}
                    <a href="mailto:support@lyrazone.com.my">
                      support@lyrazone.com.my
                    </a>
                  </li>
                  <li>
                    <i className="fa fa-phone"></i> Hotline: (+60) 14 993 6302
                  </li>
                </ul>
              </div>

              <div className="col-md-5">
                <ul className="list-inline top-data">
                  <li>
                    <a
                      href="https://www.youtube.com/channel/UC4HuaQMMFnFcLRB3IirK-Sg"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="fa top-social fa-youtube"></i>
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.facebook.com/profile.php?id=61553204526797&mibextid=kFxxJD"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="fa top-social fa-facebook"></i>
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.linkedin.com/company/lyra-zone-solutions/posts/?feedView=all"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="fa top-social fa-linkedin"></i>
                    </a>
                  </li>

                  <li>
                    <Link to="/shopping-cart" className="cart-icon-wrapper">
                      <img src="/images/cart-icon.png" alt="Cart" />
                      {cartCount > 0 && (
                        <span className="cart-badge">{cartCount}</span>
                      )}
                    </Link>
                  </li>

                  {subscriber !== "null" ? (
                    <li className="nav-item dropdown">
                      <a
                        className="nav-link dropdown-toggle smooth-scroll"
                        href="#"
                        id="navbarDropdownAbout"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                      >
                        {userImage ? (
                          <img
                            src={`${IMAGE}/uploads/subscribers/${userImage}`}
                            alt="User Avatar"
                          />
                        ) : (
                          <img
                            src="images/user-icon.png"
                            alt="User Icon"
                            style={{ width: "25px", height: "20px" }}
                          />
                        )}
                      </a>

                      <div
                        className="dropdown-menu dropdown-cust"
                        aria-labelledby="navbarDropdownAbout"
                      >
                        <a
                          className="dropdown-item"
                          onClick={() =>
                            handleProtectedNavigation("/subscriber-profile")
                          }
                        >
                          Profile
                        </a>

                        <a
                          className="dropdown-item"
                          onClick={() => handleProtectedNavigation("/orders")}
                        >
                          Orders
                        </a>
                        {!storedUser ? (
                          <a
                            className="dropdown-item"
                            onClick={() => navigate("/signin")}
                          >
                            Login
                          </a>
                        ) : (
                          <a
                            className="dropdown-item"
                            onClick={() => removeToken()}
                          >
                            Logout
                          </a>
                        )}
                      </div>
                    </li>
                  ) : (
                    <a href="/signin" className="log-top">
                      <img
                        src="images/user-icon.png"
                        alt="User Icon"
                        style={{ width: "25px", height: "20px" }}
                      />
                    </a>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <nav
        className={`navbar navbar-expand-lg navbar-light ${
          isScrolled ? "navbar-scrolled" : ""
        }`}
        id="mainNav"
        data-toggle="affix"
      >
        <div className="container lyra-header-container">
          <Link className="navbar-brand smooth-scroll" to="/">
            <img src="/images/logo-s.png" alt="logo" />
          </Link>

          <div className="header-promotion-banner">
            {!loading && validBanners.length > 0 ? (
              <div
                className="header-promotion-carousel"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <div
                  className="header-promotion-track"
                  style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                >
                  {validBanners.map((banner) => (
                    <a
                      key={banner.id}
                      href={banner.link || "#"}
                      className="header-promotion-link"
                      target={
                        banner.link?.startsWith("http") ? "_blank" : "_self"
                      }
                      rel="noreferrer"
                      aria-label={banner.title}
                    >
                      <div className="header-promotion-card">
                        <img
                          src={getBannerImage(banner.image)}
                          alt={banner.title}
                          className="header-promotion-image"
                        />
                        <span className="promotion-cta">
                          <span className="promotion-cta-text">
                            ✨ Get Promotion
                          </span>
                        </span>
                      </div>
                    </a>
                  ))}
                </div>

                {validBanners.length > 1 && (
                  <div className="header-promotion-dots">
                    {validBanners.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`header-promotion-dot ${
                          activeIndex === index ? "active" : ""
                        }`}
                        onClick={() => setActiveIndex(index)}
                        aria-label={`Go to banner ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          </div>

          <button
            className="navbar-toggler navbar-toggler-right"
            type="button"
            onClick={toggleMobileMenu}
            aria-controls="navbarResponsive"
            aria-expanded={isMobileMenuOpen}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div
            className={`navbar-collapse collapse ${isMobileMenuOpen ? "show" : ""}`}
            id="navbarResponsive"
          >
            <ul className="navbar-nav ml-auto lyra-nav-list">
              <li className="nav-item">
                <Link
                  className="nav-link smooth-scroll"
                  to="/"
                  onClick={closeMobileMenu}
                >
                  Home
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  className="nav-link smooth-scroll"
                  to="/hardware"
                  onClick={closeMobileMenu}
                >
                  Shop
                </Link>
              </li>

              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle smooth-scroll"
                  href="#"
                  id="navbarDropdownAboutMenu"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  About
                </a>
                <div
                  className="dropdown-menu dropdown-cust"
                  aria-labelledby="navbarDropdownAboutMenu"
                >
                  <Link
                    className="dropdown-item"
                    to="/about"
                    onClick={closeMobileMenu}
                  >
                    About Us
                  </Link>
                  <Link
                    className="dropdown-item"
                    to="/careers"
                    onClick={closeMobileMenu}
                  >
                    Career Opportunity
                  </Link>
                  <Link
                    className="dropdown-item"
                    to="/team"
                    onClick={closeMobileMenu}
                  >
                    Meet Our Team
                  </Link>
                  <Link
                    className="dropdown-item"
                    to="/testimonials"
                    onClick={closeMobileMenu}
                  >
                    Testimonials
                  </Link>
                </div>
              </li>

              <li className="nav-item dropdown">
                <a
                  className="nav-link dropdown-toggle smooth-scroll"
                  href="#"
                  id="navbarDropdownServices"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  Solutions
                </a>
                <div
                  className="dropdown-menu dropdown-cust"
                  aria-labelledby="navbarDropdownServices"
                >
                  <Link
                    className="dropdown-item"
                    to="/services"
                    onClick={closeMobileMenu}
                  >
                    Services
                  </Link>
                  <Link
                    className="dropdown-item"
                    to="/software"
                    onClick={closeMobileMenu}
                  >
                    Software
                  </Link>
                  <Link
                    className="dropdown-item"
                    to="/market-price-analysis"
                    onClick={closeMobileMenu}
                  >
                    Market Price Analysis
                  </Link>
                </div>
              </li>

              <li className="nav-item">
                <Link
                  className="nav-link smooth-scroll"
                  to="/testimonials"
                  onClick={closeMobileMenu}
                >
                  Clients
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className="nav-link smooth-scroll"
                  to="/support"
                  onClick={closeMobileMenu}
                >
                  Support
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className="nav-link smooth-scroll"
                  to="/contact"
                  onClick={closeMobileMenu}
                >
                  Contact
                </Link>
              </li>

              {/* Desktop search */}
              <li className="header-search-item desktop-search" ref={searchRef}>
                <button
                  type="button"
                  className="search-trigger-btn"
                  onClick={() => setIsSearchOpen((prev) => !prev)}
                  aria-label="Open search"
                >
                  <i className="fa fa-search"></i>
                </button>

                <div className={`search-open ${isSearchOpen ? "active" : ""}`}>
                  <div className="input-group animated fadeInUp">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search LyraZone..."
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    />
                    <button
                      type="button"
                      className="input-group-addon search-go-btn"
                      onClick={handleSearch}
                    >
                      Go
                    </button>
                  </div>
                </div>
              </li>

              {/* Mobile search */}
              <li className="mobile-search-box">
                {/* <div className="mobile-search-icon">
                  <i className="fa fa-search"></i>
                </div> */}

                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search LyraZone..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <button
                    type="button"
                    className="input-group-addon search-go-btn"
                    onClick={handleSearch}
                  >
                    Go
                  </button>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
